import { RealtimeServiceClient } from '@taxfyle/api-internal/internal/realtime_grpc_web_pb';
import { RealtimeEvent, StreamEventOptions } from '@taxfyle/api-internal/internal/realtime_pb';
import { Empty } from 'google-protobuf/google/protobuf/empty_pb';
import { createGrpcAuthenticator } from '../misc/grpc-authenticator';
import { defer, merge, of } from 'rxjs';
import { delay, first, map, retryWhen, share, startWith, switchMap } from 'rxjs/operators';
import { enableDevTools, fromStreamingCall } from '../utils/grpcUtil';

/**
 * Creates an observable that emits realtime events from That New New Work API.
 * The observable is shared so only a single connection is opened.
 *
 * @param config
 * @constructor
 */
export function WorkRealtime(config) {
  return defer(function () {
    var client = new RealtimeServiceClient(config.baseURL);
    var grpcAuthenticator = createGrpcAuthenticator(config);
    enableDevTools(client);
    return grpcAuthenticator.meta$.pipe(
    // We don't want to reconnect anytime the token refreshes since
    // we only need to verify the token once at connection time.
    first(), switchMap(function (meta) {
      return fromStreamingCall(function () {
        return client.streamEvents(new StreamEventOptions(), meta);
      });
    }),
    // Reconnect after 40 seconds of no activity
    realtimeRetryPolicy(40000));
  }).pipe(share());
}

/**
 * Realtime stream policy which
 * 1) Reconnects after a short delay if there are any errors.
 * 2) Reconnects if no messages are received within a specific grace period (server
 * is expected to send a ping every so often).
 * @param pingGracePeriodMs Amount of time to wait for a ping before reconnecting
 */
function realtimeRetryPolicy(pingGracePeriodMs) {
  return function (source$) {
    return source$.pipe(
    // emit a ping right away otherwise we don't start the
    // pipe which emits an error if we dont receive a message
    startWith(new RealtimeEvent().setPing(new Empty())), switchMap(function (value) {
      return merge(of(value),
      // throw an error after timeout if nothing is received
      of(1).pipe(delay(pingGracePeriodMs), map(function () {
        throw new Error("No new thing after ".concat(pingGracePeriodMs, "ms."));
      })));
    }),
    // log and retry after some delay
    retryWhen(function (errors) {
      return errors.pipe(delay(1000));
    }));
  };
}
//# sourceMappingURL=WorkRealtime.js.map
